import React, {memo, useCallback, useEffect, useRef} from 'react';
import {View, StyleSheet, AppState, AppStateStatus} from 'react-native';
import {useFocusEffect} from '@react-navigation/native';
import {BrandThemeProvider} from '../theme/BrandThemeProvider';
import {useActivityTrackerData} from '../hooks/useActivityTrackerData';
import ActivityCard from '../components/ActivityCard';
import {useDispatch} from 'react-redux';
import {
  fetchActiveOrders,
  pruneExpiredOrders,
} from '../../../../redux/orderTracker';
import {AppDispatch} from '../../../../redux/store';
import {ORDER_TRACKER_CONFIG} from '../../../../config/orderTracker';

type Props = {
  onViewReceipt: (orderId: string) => void;
  backgroundColor?: string;
};

export const ActivityTrackerSection: React.FC<Props> = memo(
  ({onViewReceipt, backgroundColor}) => {
    const dispatch = useDispatch<AppDispatch>();
    const {orders, hasTodayOrders} = useActivityTrackerData();

    const lastManualRefreshRef = useRef(0);

    const lastDayRef = useRef<string>(new Date().toDateString());

    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    const loadOrders = useCallback(() => {
      const now = Date.now();
      if (
        now - lastManualRefreshRef.current <
        ORDER_TRACKER_CONFIG.UX.MIN_REFRESH_DURATION
      ) {
        return;
      }
      lastManualRefreshRef.current = now;
      dispatch(fetchActiveOrders({force: true}));
      dispatch(pruneExpiredOrders());
    }, [dispatch]);

    useFocusEffect(
      useCallback(() => {
        loadOrders();
        return undefined;
      }, [loadOrders]),
    );

    useEffect(() => {
      const PRUNE_MS =
        ORDER_TRACKER_CONFIG.TIMERS?.TTL_PRUNE_INTERVAL_MS ?? 60_000;

      const tick = () => {
        if (AppState.currentState !== 'active') {
          return;
        }

        if (orders.length) {
          dispatch(pruneExpiredOrders());
        }

        const today = new Date().toDateString();
        if (today !== lastDayRef.current) {
          lastDayRef.current = today;
          dispatch(fetchActiveOrders({force: true}));
        }
      };

      intervalRef.current = setInterval(tick, PRUNE_MS);
      tick();

      const sub = AppState.addEventListener('change', (s: AppStateStatus) => {
        if (s === 'active') {
          tick();
        }
      });

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
        sub.remove();
      };
    }, [dispatch, orders.length]);

    if (!hasTodayOrders) {
      return null;
    }

    return (
      <View
        style={[
          styles.container,
          {backgroundColor: backgroundColor ?? 'transparent'},
        ]}
      >
        <View style={styles.list}>
          {orders.map((order) => (
            <ActivityCard
              key={order.id}
              order={order}
              onViewReceipt={onViewReceipt}
            />
          ))}
        </View>
      </View>
    );
  },
);

const styles = StyleSheet.create({
  container: {width: '100%'},
  list: {paddingHorizontal: 16, paddingVertical: 8},
});

const WithTheme: React.FC<
  React.ComponentProps<typeof ActivityTrackerSection>
> = (p) => (
  <BrandThemeProvider>
    <ActivityTrackerSection {...p} />
  </BrandThemeProvider>
);

export default WithTheme;
